import { Record } from 'immutable';
import { isUndefined } from 'lodash';
import { LocaleService } from '@sm360/widget-commons-core-services';

import { DATA_TYPES, DEFAULT_MODEL_TRIM_CODE, ITEM_FLAG } from 'commons-vehicle/constants/dataTypes.js';
import {
    CONSUMPTION_TYPE,
    CONSUMPTION_UNIT,
    VEHICLE_STATUS,
    VEHICLE_CONDITION,
} from 'commons-vehicle/constants/vehicle.js';
import { ODOMETER } from 'commons-vehicle/constants/currencyFormats.js';
import InventoryMultimediaModelV25 from 'commons-vehicle/Vehicle/models/InventoryMultimediaModelV25.js';
import InventoryAccessoriesAndOptionModelV25 from 'commons-vehicle/Vehicle/models/InventoryAccessoriesAndOptionModelV25.js';
import PurchaseMethodsModelV25 from 'commons-vehicle/PurchaseMethodsV25/models/PurchaseMethodsModelV25.js';
import { ONE_DAY_TIMESTAMP } from 'commons-vehicle/constants/dataConversions.js';

const VehicleRecord = Record({
    // Need a new Model for accessories and options ?
    accessoriesAndOptions: [new InventoryAccessoriesAndOptionModelV25()],
    asTraded: false,
    bodyClassification: {
        name: '',
        slug: '',
    },
    bodyColourStatus: 0,
    bodyDimension: {
        name: '',
        slug: '',
    },
    bodyStyle: {
        name: '',
        slug: '',
    },
    bodyStyleCode: '',
    carProof: '',
    category: '',
    certified: false,
    clearance: false,
    comingSoon: false,
    commercialVehicle: false,
    configuration: {
        addedItems: [],
        packageDescription: '',
        packageCode: '',
        modelTrimFeatures: [],
        modelTrimStandardFeatures: [],
        packageOptionsAndAccessories: [],
        packageStandardFeatures: [],
        packageFeatures: [],
    },
    configurationId: 0,
    configurationToken: '',
    consumption: [
        { consumptionType: CONSUMPTION_TYPE.IN_CITY, consumption: 0, consumptionUnit: CONSUMPTION_UNIT.L_KM },
    ],
    country: '',
    custom: false,
    dateDelivery: '',
    dateEntry: '',
    daysInInventory: 0,
    dealerTrackHash: '',
    demo: false,
    description: '',
    discontinued: false,
    distance: 0,
    driveTrain: {
        abbreviation: '',
        id: 0,
        name: '',
        slug: '',
    },
    engine: {
        description: '',
        fuelConsumption: {
            city: '',
            highway: '',
        },
        id: 0,
        name: '',
        power: '',
        type: '',
    },
    extendedWarranty: false,
    exteriorColor: {
        code: '',
        color: '',
        colorDescription: '',
        colorDescriptionEn: '',
        colorDescriptionFr: '',
        colorEn: '',
        colorFr: '',
        hexValue: '',
        id: 0,
    },
    exteriorColourPhotos: {
        code: '',
        description: '',
        front: '',
        rear: '',
        side: '',
        tile: '',
    },
    extraOptions: '',
    featured: false,
    features: [
        {
            icon: '',
            description: '',
        },
    ],
    fuel: {
        id: 0,
        name: '',
        slug: '',
    },
    hasPictures: false,
    hasPrice: false,
    id: '',
    inspected: false,
    interiorColor: {
        code: '',
        color: '',
        colorDescription: '',
        colorDescriptionEn: '',
        colorDescriptionFr: '',
        colorEn: '',
        colorFr: '',
        hexValue: '',
        id: 0,
    },
    inventoryId: 0,
    listPrice: 0,
    locked: false,
    make: {
        id: 0,
        name: '',
        slug: '',
    },
    model: {
        id: 0,
        name: '',
        slug: '',
    },
    modelTrim: {
        bestSellingPrice: '',
        id: 0,
        name: '',
        nameExtension: '',
        slug: '',
    },
    modelTrimCode: '',
    multimedia: new InventoryMultimediaModelV25(), // Model has to be created
    newCarTrimId: 0,
    newVehicle: false,
    odometer: 0,
    onConsignment: false,
    onHold: false,
    online: true,
    packageCode: '',
    packageDescription: '',
    pageViewSummary: {
        lastViewDate: '',
        recentViewCount: 0,
        totalViewCount: 0,
    },
    purchaseMethods: new PurchaseMethodsModelV25(), // Model has to be created
    primaryOrganizationUnit: {
        name: '',
        id: 0,
    },
    salePrice: 0,
    severelyDamagedVehicle: false,
    showroomVehicleId: '',
    sold: false, // Not directly provided by Vehicle API (use this.vehicleStatus === SOLD instead)
    stickerStatusInTransit: false,
    stickerStatusOnOrder: false,
    stockNumber: '',
    tagline: '',
    transmission: {
        id: 0,
        name: '',
        speeds: '',
        transGears: 0,
    },
    vehicleCondition: '',
    vehicleId: null,
    vehicleInventoryBuild: {
        id: 0,
        lastPriceUpdate: '',
        modelTrimCodeManufacturer: '',
        optionCodes: [''],
        packageCode: '',
    },
    vehicleInventoryStatus: VEHICLE_STATUS.FOR_SALE,
    vehicleStatus: VEHICLE_STATUS.FOR_SALE,
    vin: '',
    wholesale: 0,
    year: 0,
    seating: 0,
    doors: 0,
    warranty: {},
    dateSold: null,
    options: [], // VDP Used only
    carfax: {
        BadgesList: [
            {
                BadgeList: [],
                BadgesImageUrl: '',
                VhrReportUrl: '',
            },
        ],
    },
    pastOwner: {
        id: 0,
        descriptionFr: '',
        descriptionEn: '',
        description: '',
    },
    previousUse: {
        id: 0,
        descriptionFr: '',
        descriptionEn: '',
        description: '',
    },
});

class VehicleModelV25 extends VehicleRecord {
    constructor(vehicle) {
        if (vehicle) {
            const vehicleDefaults = {
                ...vehicle,
                vin: vehicle?.serialNo || vehicle?.vin,
                stockNumber: vehicle?.stockNo || vehicle?.stockNumber,
                modelTrim: vehicle?.trim || vehicle?.modelTrim,
                transmission: vehicle?.transmissionDetails || vehicle?.transmission,
                driveTrain: vehicle?.driveTrainDetails || vehicle?.driveTrain,
                online: vehicle?.active || vehicle?.online || true,
                seating: vehicle?.passengers || vehicle?.seating,
                engine: vehicle?.engine?.description
                    ? { ...vehicle.engine, name: vehicle.engine.description }
                    : vehicle?.engine,
                warranty: vehicle?.warrantyDescription || vehicle?.warranty,
                onHold: vehicle?.locked || vehicle?.onHold,
            };

            if (vehicle?.multimedia) {
                if (!(vehicle.multimedia instanceof InventoryMultimediaModelV25)) {
                    vehicleDefaults.multimedia = new InventoryMultimediaModelV25(vehicle.multimedia);
                }
            } else {
                vehicleDefaults.multimedia = new InventoryMultimediaModelV25();
            }

            if (!(vehicle?.purchaseMethods instanceof PurchaseMethodsModelV25)) {
                if (vehicle?.paymentOptions)
                    vehicleDefaults.purchaseMethods = new PurchaseMethodsModelV25(vehicle.paymentOptions);
                else if (vehicle?.purchaseMethods)
                    vehicleDefaults.purchaseMethods = new PurchaseMethodsModelV25(vehicle.purchaseMethods);
            }

            if (vehicle?.paymentOptions && !(vehicle?.purchaseMethods instanceof PurchaseMethodsModelV25)) {
                vehicleDefaults.purchaseMethods = new PurchaseMethodsModelV25(vehicle.paymentOptions);
            }

            if (
                vehicle.vehicleCondition === VEHICLE_CONDITION.NEW ||
                vehicle.vehicleCondition === VEHICLE_CONDITION.DEMO
            )
                vehicleDefaults.newVehicle = true;

            if (vehicle.vehicleCondition === VEHICLE_CONDITION.DEMO) vehicle.demo = true;

            if (vehicle?.id) vehicleDefaults.showroomVehicleId = vehicle.id;
            if (vehicle?.newVehicle || vehicleDefaults?.newVehicle) {
                vehicleDefaults.vehicleCondition = vehicle.demo ? VEHICLE_CONDITION.DEMO : VEHICLE_CONDITION.NEW;
            } else {
                vehicleDefaults.vehicleCondition = vehicle.certified
                    ? VEHICLE_CONDITION.CERTIFIED
                    : VEHICLE_CONDITION.USED;
            }

            super(vehicleDefaults);
        } else {
            super();
        }
    }

    isSold() {
        return this.vehicleInventoryStatus === VEHICLE_STATUS.SOLD || this.sold;
    }

    isDeleted() {
        return this.vehicleInventoryStatus === VEHICLE_STATUS.DELETED;
    }

    isAvailable() {
        return !(this.isSold() || this.onHold || this.discontinued || !this.online);
    }

    isModelContainsMake() {
        return this?.model?.name?.includes(this?.make?.name);
    }

    isThisVehicleModel(...modelNames) {
        return modelNames.reduce((acc, modelName) => acc || this.model?.name === modelName, true);
    }

    getIsVehicleNew() {
        return this.newVehicle || this.vehicleCondition === 'new' || this.vehicleCondition === 'newDemonstrator';
    }

    getMake() {
        return this.make?.name || '';
    }

    getModel() {
        return this.model?.name || '';
    }

    getYearMakeModel() {
        return this.year > 0
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.getModel(),
              })
            : '';
    }

    getYearMakeModelTrim() {
        if (this.year === 0) {
            return '';
        }

        const modelTrim = this.modelTrim.name;

        return modelTrim
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL_TRIM', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.getModel(),
                  modelTrim,
              })
            : LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.getModel(),
              });
    }

    getYearModel() {
        if (this.year === 0) {
            return '';
        }

        return LocaleService.t('VEHICLE_MODEL.YEAR_MODEL', { year: this.year, model: this.getModel() });
    }

    getYear() {
        return this?.year || 0;
    }

    getTrim() {
        return this?.modelTrim.name || '';
    }

    getModelTrimCode() {
        return this?.modelTrimCode || '';
    }

    getStockNumber() {
        return this?.stockNumber || '';
    }

    getVehicleId() {
        return this?.vehicleId || null;
    }

    getYearModelTrim() {
        if (this.year === 0) {
            return '';
        }

        const modelTrim = this.configuration !== null ? this.configuration.modelTrim.description : this.modelTrim.name;
        return this.modelTrim && this.modelTrim !== this.getModel()
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MODEL_TRIM', {
                  year: this.year,
                  model: this.getModel(),
                  modelTrim,
              })
            : LocaleService.t('VEHICLE_MODEL.YEAR_MODEL', {
                  year: this.year,
                  model: this.getModel(),
              });
    }

    // Not usable for Vehicle API
    getDiscount() {
        return this.discount > 250 ? this.discount : 0;
    }

    getOdometer() {
        if (!this.odometer) {
            return '';
        }

        const odometer = LocaleService.l(this.odometer, ODOMETER);
        return `${odometer} km`;
    }

    // Not usable for Vehicle API
    getFuelConsumptionAverage() {
        return Math.round(((this.fuelCity + this.fuelHighway) / 2) * 10) / 10;
    }

    getConsumptionValue(consumption) {
        if (consumption && consumption?.consumption && consumption?.consumptionUnit) {
            return consumption.consumption;
        }
        return '';
    }

    getConsumptionInCity() {
        const consumption = this.consumption?.find((c) => c.consumptionType === CONSUMPTION_TYPE.IN_CITY);
        return this.getConsumptionValue(consumption);
    }

    getConsumptionOnHighway() {
        const consumption = this.consumption?.find((c) => c.consumptionType === CONSUMPTION_TYPE.ON_HIGHWAY);
        return this.getConsumptionValue(consumption);
    }

    getConsumptionElectric() {
        const consumption = this.consumption?.find((c) => c.consumptionType === CONSUMPTION_TYPE.ELECTRIC);
        return this.getConsumptionValue(consumption);
    }

    getDriveTrain() {
        return this?.driveTrain?.name || '';
    }

    getEngine() {
        return this?.engine?.description || '';
    }

    getTransmission() {
        if (this.transmission?.speeds)
            return this.transmission ? `${this.transmission.name} ${this.transmission.speeds}` : '';

        return this?.transmission?.name || '';
    }

    getPackageName() {
        return this.configuration?.packageDescription || '';
    }

    // May need to be refactored for new PaymentCalculator API
    getPaymentCalculatorVehicleParameters() {
        return {
            vehicleCondition: this.vehicleCondition,
            year: this.year,
            make: this.make,
            model: this.model,
            bodyStyleCode: this.bodyStyleCode,
            driveTrainCode: this.driveTrainCode,
            odometer: this.odometer,
            sellingPrice: this.sellingPrice,
            vin: this.vin,
        };
    }

    getDefaultVehicleQuoteItem() {
        return {
            dataType: DATA_TYPES.FEATURE.DATA_TYPE_ID,
            typeID: DATA_TYPES.FEATURE.TYPES.MODEL_TRIM,
            code: DEFAULT_MODEL_TRIM_CODE,
            flag: ITEM_FLAG.SELECTED,
            amounts: {
                retail: this.sellingPrice,
            },
            description: {
                descriptions: [this.model],
            },
        };
    }

    getWarranty() {
        const supplementary = {
            date: this.warranty?.supplementaryWarrantyDate,
            km: this.warranty?.supplementaryWarrantyKm,
            info: this.warranty?.warrantySupp,
        };
        const warrantyClass = { label: this.warranty?.warrantyClass };
        return {
            supplementary,
            warrantyClass,
        };
    }

    isSoldShown(soldDaysShown) {
        if (this.isSold()) {
            if (isUndefined(soldDaysShown) || soldDaysShown < 0) {
                console.error(
                    "VDP Used - Couldn't determine if Sold vehicle should be displayed, the XMS parameter soldDaysShown is undefined/incorrect"
                );
                return true;
            }

            const dateSold = Date.parse(this.dateSold);
            // If dateSold is undefined, always show vehicle
            if (!dateSold) return true;

            // If dateSold is defined and soldDaysShown is 0 then we should never show vehicle
            if (dateSold && soldDaysShown === 0) return false;

            // If the current time is beyond the time limit (starting at dateSold): do not show vehicle
            const timeNow = new Date().setUTCHours(0, 0, 0, 0);
            return timeNow < dateSold + soldDaysShown * ONE_DAY_TIMESTAMP;
        }

        // If not sold, always show vehicle
        return true;
    }

    getShowroomVehicle() {
        return {
            make: this.getMake(),
            model: this.getModel(),
            odometer: this.odometer,
            showroomVehicleId: this.showroomVehicleId,
            stockNumber: this.stockNumber,
            vehicleCondition: this.getIsVehicleNew() ? 'new' : 'used',
            vehicleId: this.vehicleId,
            vin: this.vin,
            year: this.year,
        };
    }

    getOptionsAndAccessories() {
        if (!this.configuration?.addedItems?.items) return [];
        return this.configuration?.addedItems.items.filter(
            (item) => item.category === 'option' || item.category === 'accessory'
        );
    }
}

export default VehicleModelV25;
